/**
 * Status Explanations Utility
 * Centralized location for all status explanation functions
 * Provides consistent, reusable status descriptions across the application
 */

/**
 * Get explanation for lead status
 * @param {string} status - The lead status
 * @returns {string} Explanation of what the status means
 */
export const getLeadStatusExplanation = (status) => {
  switch (status) {
    case "new":
      return "New lead that has been submitted and is awaiting initial review. This is the first stage in the lead qualification process.";
    case "contacted":
      return "Lead has been contacted by the sales team. Initial communication has been established and further qualification is in progress.";
    case "qualified":
      return "Lead has been qualified as a potential customer. Requirements have been assessed and the lead meets basic criteria.";
    case "accepted":
      return "Lead has been accepted and approved for quote generation. Customer requirements are confirmed and ready for pricing.";
    case "rejected":
      return "Lead has been rejected due to not meeting qualification criteria, budget constraints, or other business reasons.";
    case "converted":
      return "Lead has been successfully converted to a quote or order. This represents a successful progression through the sales funnel.";
    default:
      return "Status information not available.";
  }
};

/**
 * Get explanation for quote status
 * @param {string} status - The quote status
 * @returns {string} Explanation of what the status means
 */
export const getQuoteStatusExplanation = (status) => {
  switch (status) {
    case "pending":
      return "Quote has been created and is awaiting review and cost estimation. Initial stage of quote preparation.";
    case "sent":
      return "Quote has been generated and sent to the customer. Awaiting customer response or decision.";
    case "accepted":
      return "Customer has accepted the quote. Ready to proceed with order creation and fulfillment.";
    case "rejected":
      return "Customer has rejected the quote. May require follow-up, quote revision, or further negotiation.";
    case "ordered":
      return "Quote has been successfully converted to an order. The business process is now in fulfillment stage.";
    case "expired":
      return "Quote has expired due to time limitations. May need to be regenerated or updated with new pricing.";
    default:
      return "Status information not available.";
  }
};

/**
 * Get explanation for order status
 * @param {string} status - The order status
 * @returns {string} Explanation of what the status means
 */
export const getOrderStatusExplanation = (status) => {
  switch (status) {
    case "pending":
      return "Order has been created and is awaiting processing. Initial stage of order fulfillment.";
    case "booked":
      return "Order has been confirmed and booked. Ready for processing and preparation.";
    case "in_progress":
      return "Order is currently being processed and prepared for delivery. Active fulfillment stage.";
    case "arrived_at_storage":
      return "Items have been successfully picked up and have arrived at our storage facility. Items are securely stored and being prepared for final delivery.";
    case "in_transit_from_storage":
      return "Items have been collected from storage and are currently in transit to the delivery address. Delivery is in progress.";
    case "completed":
      return "Order has been successfully delivered and completed. Customer has received the service.";
    case "cancelled":
      return "Order has been cancelled either by customer request or due to operational issues.";
    default:
      return "Status information not available.";
  }
};

/**
 * Get explanation for feedback status
 * @param {string} status - The feedback status
 * @returns {string} Explanation of what the status means
 */
export const getFeedbackStatusExplanation = (status) => {
  switch (status) {
    case "pending":
      return "Feedback request has been sent to the customer. Awaiting customer response and rating.";
    case "completed":
      return "Customer has provided feedback and rating. Feedback process is complete.";
    case "expired":
      return "Feedback request has expired without customer response. May need to be resent.";
    default:
      return "Status information not available.";
  }
};

/**
 * Get explanation for priority level
 * @param {string} priority - The priority level
 * @returns {string} Explanation of what the priority means
 */
export const getPriorityExplanation = (priority) => {
  switch (priority) {
    case "high":
      return "High priority orders require immediate attention and should be processed first.";
    case "medium":
      return "Medium priority orders should be processed within normal business hours.";
    case "low":
      return "Low priority orders can be processed when resources are available.";
    default:
      return "Priority level information not available.";
  }
};

/**
 * Get explanation for platform status
 * @param {string} status - The platform status
 * @returns {string} Explanation of what the status means
 */
export const getPlatformStatusExplanation = (status) => {
  switch (status) {
    case "active":
      return "Platform is operational and ready to send/receive messages.";
    case "inactive":
      return "Platform is currently offline or experiencing issues.";
    case "warning":
      return "Platform is operational but experiencing minor issues or limitations.";
    default:
      return "Platform status information not available.";
  }
};
